import React, { useState, useRef, useEffect } from 'react';
import { getEndpoint } from '../config/api';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Animated,
  StatusBar,
  Dimensions,
  Platform,
  SafeAreaView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { COLORS } from '../constants/theme';

const { width, height } = Dimensions.get('window');

interface ForgotPasswordScreenProps {
  onBackPress: () => void;
  onSuccess: () => void;
}

export const ForgotPasswordScreen: React.FC<ForgotPasswordScreenProps> = ({ 
  onBackPress, 
  onSuccess 
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const slideInAnimation = useRef(new Animated.Value(width)).current;
  const fadeInAnimation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.parallel([
              Animated.timing(slideInAnimation, {
          toValue: 0,
          duration: 800,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(fadeInAnimation, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: Platform.OS !== 'web',
        }),
    ]);

    animation.start();
  }, []);

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setErrorMessage('Please enter your email address');
      return;
    }

    if (!isValidEmail(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await fetch(getEndpoint('/api/auth/request-reset'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccessMessage('If the email exists, a recovery link will be sent');
        setTimeout(() => {
          onSuccess();
        }, 3000);
      } else {
        setErrorMessage(data.message || 'Failed to send reset email');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      setErrorMessage('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar 
        barStyle="dark-content" 
        backgroundColor="#FFFFFF"
        translucent={Platform.OS === 'android'}
      />
      
      <Animated.View
        style={[
          styles.content,
          {
            transform: [{ translateX: slideInAnimation }],
            opacity: fadeInAnimation,
          },
        ]}
      >
        {/* Header */}
        {/* Title */}
        <Text style={styles.title}>Forgot Password</Text>
        <Text style={styles.subtitle}>
          Enter your email address and we'll send you a link to reset your password.
        </Text>

        {/* Error Message */}
        {errorMessage ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{errorMessage}</Text>
          </View>
        ) : null}

        {/* Success Message */}
        {successMessage ? (
          <View style={styles.successContainer}>
            <Text style={styles.successText}>{successMessage}</Text>
          </View>
        ) : null}

        {/* Form */}
        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Email address"
              placeholderTextColor="#9CA3AF"
              value={email}
              onChangeText={(text) => {
                setEmail(text);
                setErrorMessage('');
                setSuccessMessage('');
              }}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isLoading}
            />
          </View>

          {/* Submit Button */}
          <TouchableOpacity
            style={[
              styles.submitButton,
              isLoading && styles.submitButtonDisabled
            ]}
            onPress={handleForgotPassword}
            activeOpacity={0.8}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Text style={styles.submitButtonText}>Send Reset Link</Text>
            )}
          </TouchableOpacity>

          {/* Back to Login */}
          <TouchableOpacity
            style={styles.backToLoginButton}
            onPress={onBackPress}
            activeOpacity={0.7}
            disabled={isLoading}
          >
            <Text style={styles.backToLoginText}>Back to Login</Text>
          </TouchableOpacity>
        </View>
      </Animated.View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    flex: 1,
    paddingHorizontal: Platform.select({ ios: 24, android: 24, default: 20 }),
    paddingTop: Platform.select({ ios: 130, android: 130, default: 230 }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Platform.select({ ios: 40, android: 40, default: 32 }),
  },
  backButton: {
    width: Platform.select({ ios: 40, android: 40, default: 36 }),
    height: Platform.select({ ios: 40, android: 40, default: 36 }),
    borderRadius: Platform.select({ ios: 20, android: 20, default: 18 }),
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  backButtonText: {
    fontSize: Platform.select({ ios: 24, android: 24, default: 20 }),
    color: '#374151',
    fontWeight: '300',
  },
  title: {
    fontSize: Platform.select({ ios: 32, android: 32, default: 28 }),
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: Platform.select({ ios: 16, android: 16, default: 12 }),
    textAlign: 'center',
    fontFamily: 'Montserrat_700Bold',
  },
  subtitle: {
    fontSize: Platform.select({ ios: 16, android: 16, default: 14 }),
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: Platform.select({ ios: 24, android: 24, default: 20 }),
    marginBottom: Platform.select({ ios: 40, android: 40, default: 32 }),
    fontFamily: 'Montserrat_400Regular',
  },
  errorContainer: {
    backgroundColor: '#FEF2F2',
    borderWidth: 1,
    borderColor: '#FECACA',
    borderRadius: Platform.select({ ios: 8, android: 8, default: 6 }),
    padding: Platform.select({ ios: 12, android: 12, default: 10 }),
    marginBottom: Platform.select({ ios: 20, android: 20, default: 16 }),
  },
  errorText: {
    color: '#DC2626',
    fontSize: Platform.select({ ios: 14, android: 14, default: 12 }),
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
  successContainer: {
    backgroundColor: '#F0FDF4',
    borderWidth: 1,
    borderColor: '#BBF7D0',
    borderRadius: Platform.select({ ios: 8, android: 8, default: 6 }),
    padding: Platform.select({ ios: 12, android: 12, default: 10 }),
    marginBottom: Platform.select({ ios: 20, android: 20, default: 16 }),
  },
  successText: {
    color: '#16A34A',
    fontSize: Platform.select({ ios: 14, android: 14, default: 12 }),
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
  form: {
    flex: 1,
  },
  inputContainer: {
    marginBottom: Platform.select({ ios: 32, android: 32, default: 24 }),
  },
  input: {
    height: Platform.select({ ios: 56, android: 56, default: 48 }),
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: Platform.select({ ios: 12, android: 12, default: 10 }),
    paddingHorizontal: Platform.select({ ios: 16, android: 16, default: 14 }),
    fontSize: Platform.select({ ios: 16, android: 16, default: 14 }),
    color: '#111827',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Montserrat_400Regular',
  },
  submitButton: {
    backgroundColor: COLORS.primary,
    height: Platform.select({ ios: 56, android: 56, default: 48 }),
    borderRadius: Platform.select({ ios: 12, android: 12, default: 10 }),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: Platform.select({ ios: 24, android: 24, default: 20 }),
  },
  submitButtonDisabled: {
    backgroundColor: '#9CA3AF',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: Platform.select({ ios: 18, android: 18, default: 16 }),
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  backToLoginButton: {
    alignItems: 'center',
    paddingVertical: Platform.select({ ios: 16, android: 16, default: 12 }),
  },
  backToLoginText: {
    color: COLORS.primary,
    fontSize: Platform.select({ ios: 16, android: 16, default: 14 }),
    fontWeight: '500',
    fontFamily: 'Montserrat_500Medium',
  },
});
